// src/reader/PdfViewer.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import api from "../api/client";
import { getPdfSource } from "../utils/pdfSource";
import "../styles/reader.css";

/* PDF worker */
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

/* =========================
   Pure helpers (OUTSIDE component)
   ========================= */
function looksLikeRangeCacheFailure(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  if (msg.includes("err_cache_operation_not_supported")) return true;
  if (msg.includes("failed to fetch") || msg.includes("networkerror")) return true;
  if (msg.includes("aborterror")) return true;
  return false;
}

function getPageWrapperFromSelection(selection) {
  const node = selection?.anchorNode;
  if (!node) return null;
  const el = node.nodeType === 1 ? node : node.parentElement;
  if (!el) return null;
  return el.closest(".pdf-page-wrapper");
}

function computeOffsetsFromRange(textLayerEl, range) {
  const spans = Array.from(textLayerEl.querySelectorAll("span"));
  if (!spans.length) return { start: null, end: null };

  function findSpan(elOrNode) {
    const el = elOrNode?.nodeType === 1 ? elOrNode : elOrNode?.parentElement;
    return el?.closest?.("span") || null;
  }

  const startSpan = findSpan(range.startContainer);
  const endSpan = findSpan(range.endContainer);
  if (!startSpan || !endSpan) return { start: null, end: null };

  const startIdx = spans.indexOf(startSpan);
  const endIdx = spans.indexOf(endSpan);
  if (startIdx < 0 || endIdx < 0) return { start: null, end: null };

  const startLocal = range.startContainer?.nodeType === 3 ? range.startOffset : 0;
  const endLocal =
    range.endContainer?.nodeType === 3
      ? range.endOffset
      : endSpan.textContent?.length || 0;

  let start = 0;
  for (let i = 0; i < startIdx; i++) start += (spans[i].textContent || "").length;
  start += startLocal;

  let end = 0;
  for (let i = 0; i < endIdx; i++) end += (spans[i].textContent || "").length;
  end += endLocal;

  if (end < start) [start, end] = [end, start];
  return { start, end };
}

function clearExistingMarks(textLayerEl) {
  const marks = textLayerEl.querySelectorAll("mark.pdf-highlight");
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
}

function applyHighlightByOffsets(textLayerEl, start, end, noteId, color = "yellow") {
  const spans = Array.from(textLayerEl.querySelectorAll("span"));
  if (spans.length === 0) return;

  let pos = 0;
  for (const span of spans) {
    const spanText = span.textContent || "";
    const spanStart = pos;
    const spanEnd = pos + spanText.length;
    pos += spanText.length;

    if (spanEnd <= start) continue;
    if (spanStart >= end) break;

    const localStart = Math.max(0, start - spanStart);
    const localEnd = Math.min(spanText.length, end - spanStart);

    const before = spanText.slice(0, localStart);
    const middle = spanText.slice(localStart, localEnd);
    const after = spanText.slice(localEnd);

    span.textContent = "";
    if (before) span.appendChild(document.createTextNode(before));

    const mark = document.createElement("mark");
    mark.className = `pdf-highlight pdf-highlight--${color}`;
    mark.textContent = middle;
    mark.dataset.noteId = String(noteId);
    span.appendChild(mark);

    if (after) span.appendChild(document.createTextNode(after));
  }
}

/* ---------- Find helpers (simple + safe) ---------- */
function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function clearFindMarks(textLayerEl) {
  const marks = textLayerEl.querySelectorAll("mark.pdf-find");
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
}

/**
 * Simple highlight: highlights matches *within individual spans*.
 * (Won't catch matches spanning multiple spans, but works well in practice and is fast.)
 */
function applyFindHighlight(textLayerEl, query) {
  const q = String(query || "").trim();
  if (!q) return;

  clearFindMarks(textLayerEl);

  const spans = Array.from(textLayerEl.querySelectorAll("span"));
  if (!spans.length) return;

  const re = new RegExp(escapeRegExp(q), "gi");

  for (const span of spans) {
    const t = span.textContent || "";
    if (!t) continue;
    if (!re.test(t)) continue;

    // reset lastIndex (global regex)
    re.lastIndex = 0;

    const parts = [];
    let last = 0;
    let m;

    while ((m = re.exec(t)) !== null) {
      const start = m.index;
      const end = start + m[0].length;

      if (start > last) parts.push(document.createTextNode(t.slice(last, start)));

      const mark = document.createElement("mark");
      mark.className = "pdf-find";
      mark.textContent = t.slice(start, end);
      parts.push(mark);

      last = end;

      // safety
      if (re.lastIndex === m.index) re.lastIndex++;
    }

    if (last < t.length) parts.push(document.createTextNode(t.slice(last)));

    span.textContent = "";
    for (const p of parts) span.appendChild(p);
  }
}

/** Defer state updates so we don't setState synchronously in effect bodies */
function defer(fn) {
  if (typeof queueMicrotask === "function") queueMicrotask(fn);
  else setTimeout(fn, 0);
}

/* =========================================================
   COMPONENT
   ========================================================= */
export default function PdfViewer({
  documentId,
  startPage = 1,
  maxAllowedPage = null,
  onPreviewLimitReached,
  onRegisterApi,
}) {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(startPage || 1);

  // pdf proxy (from react-pdf)
  const pdfRef = useRef(null);

  // ✅ Always-current page value for external polling / reads
  const currentPageRef = useRef(startPage || 1);
  useEffect(() => {
    currentPageRef.current = page;
  }, [page]);

  // ✅ Parent subscriptions (event-driven)
  const pageChangeSubsRef = useRef(new Set());
  const emitPageChange = useCallback((nextPage) => {
    for (const fn of pageChangeSubsRef.current) {
      try {
        fn(nextPage);
      } catch {
        // ignore subscriber errors
      }
    }
  }, []);

  const [ready, setReady] = useState(false);
  const [highlights, setHighlights] = useState([]);

  /* ---------------- Loading UX ---------------- */
  const [loadPct, setLoadPct] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [safeMode, setSafeMode] = useState(false);
  const [docKey, setDocKey] = useState(0);

  /* ---------------- Reading Progress ---------------- */
  const [resumeLoaded, setResumeLoaded] = useState(false);
  const lastSavedPageRef = useRef(null);
  const readingStartRef = useRef(0);
  const pageUpdateTimeoutRef = useRef(null);

  const resumeTargetRef = useRef(null);
  const resumeAppliedRef = useRef(false);
  const pendingJumpRef = useRef(null);

  /* ---------------- Reader Preferences ---------------- */
  const [zoom, setZoom] = useState(1);
  const [darkMode, setDarkMode] = useState(false);

  /* ---------------- Go to Page (popover) ---------------- */
  const [showPageJump, setShowPageJump] = useState(false);
  const [pageJumpValue, setPageJumpValue] = useState("");
  const [pageJumpError, setPageJumpError] = useState("");
  const pageJumpInputRef = useRef(null);

  /* ---------------- Find in PDF ---------------- */
  const [showFind, setShowFind] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [findBusy, setFindBusy] = useState(false);
  const [findError, setFindError] = useState("");
  const [findResults, setFindResults] = useState([]); // array of page numbers (unique)
  const [findActiveIdx, setFindActiveIdx] = useState(0);
  const findInputRef = useRef(null);

  // text cache per page
  const pageTextCacheRef = useRef(new Map()); // pageNumber -> string
  const findAbortRef = useRef({ aborted: false });

  const isProgrammaticNavRef = useRef(false);

  /* ---------------- Notes ---------------- */
  const [showNoteBox, setShowNoteBox] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [notes, setNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState("");

  const [activeNoteId, setActiveNoteId] = useState(null);
  const [flashNoteId, setFlashNoteId] = useState(null);

  const [highlightColor, setHighlightColor] = useState("yellow");
  const [highlightMeta, setHighlightMeta] = useState(null);

  const noteRefs = useRef({});

  /* Scroll + observer */
  const scrollRootRef = useRef(null);
  const pageObserverRef = useRef(null);

  const observerReadyRef = useRef(false);
  const fallbackIdleTimerRef = useRef(null);

  const snapTimeoutRef = useRef(null);
  const isUserScrollingRef = useRef(false);

  const scrollRafRef = useRef(null);
  const lastSensorPageRef = useRef(null);

  const pageElsRef = useRef({});
  const previewLimitTriggeredRef = useRef(false);

  const [renderLimit, setRenderLimit] = useState(10);

  const fileSource = useMemo(() => getPdfSource(documentId), [documentId]);

  const pdfOptions = useMemo(
    () =>
      safeMode
        ? { disableRange: true, disableStream: true }
        : { disableRange: false, disableStream: false },
    [safeMode]
  );

  const highlightsByPage = useMemo(() => {
    const map = {};
    for (const h of highlights) {
      if (!map[h.page]) map[h.page] = [];
      map[h.page].push(h);
    }
    return map;
  }, [highlights]);

  const allowedMaxPage = useMemo(() => {
    if (!numPages) return maxAllowedPage ?? null;
    return maxAllowedPage ? Math.min(maxAllowedPage, numPages) : numPages;
  }, [numPages, maxAllowedPage]);

  const registerPageEl = useCallback((pageNumber, el) => {
    if (el) pageElsRef.current[pageNumber] = el;
  }, []);

  const scrollToPage = useCallback((targetPage, behavior = "auto") => {
    const el = pageElsRef.current[targetPage];
    if (!el) return false;
    el.scrollIntoView({ behavior, block: "start" });
    return true;
  }, []);

  /* ==================================================
     RESET WHEN DOCUMENT CHANGES
     ================================================== */
  useEffect(() => {
    clearTimeout(pageUpdateTimeoutRef.current);
    clearTimeout(fallbackIdleTimerRef.current);
    clearTimeout(snapTimeoutRef.current);
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = null;

    // reset find cache/state
    pageTextCacheRef.current = new Map();
    findAbortRef.current.aborted = true;
    defer(() => {
      setShowFind(false);
      setFindQuery("");
      setFindResults([]);
      setFindActiveIdx(0);
      setFindBusy(false);
      setFindError("");
    });

    defer(() => {
      setNumPages(null);
      setReady(false);
      setLoadPct(0);
      setLoadError("");
      setRenderLimit(10);

      setSafeMode(false);
      setDocKey((k) => k + 1);

      const sp = startPage || 1;
      setPage(sp);
      currentPageRef.current = sp;
      emitPageChange(sp);

      setResumeLoaded(false);
      pendingJumpRef.current = null;
      resumeTargetRef.current = null;
      resumeAppliedRef.current = false;

      previewLimitTriggeredRef.current = false;

      observerReadyRef.current = false;
      lastSensorPageRef.current = null;
      pageElsRef.current = {};
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  /* ==================================================
     LOAD READING PROGRESS
     ================================================== */
  useEffect(() => {
    const ctrl = new AbortController();

    defer(() => {
      setResumeLoaded(false);
      pendingJumpRef.current = null;
      resumeTargetRef.current = null;
      resumeAppliedRef.current = false;
    });

    api
      .get(`/reading-progress/${documentId}`, { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        const saved = res.data?.pageNumber;
        if (saved && saved > 0) {
          pendingJumpRef.current = saved;
          resumeTargetRef.current = saved;
        }
        defer(() => setResumeLoaded(true));
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        console.warn("reading-progress load failed:", err);
        defer(() => setResumeLoaded(true));
      });

    return () => {
      ctrl.abort();
    };
  }, [documentId]);

  /* ==================================================
     SAVE READING PROGRESS
     ================================================== */
  useEffect(() => {
    if (!ready || !numPages || !resumeLoaded) return;
    if (lastSavedPageRef.current === page) return;

    lastSavedPageRef.current = page;

    const now = Date.now();
    const secondsReadDelta = Math.round((now - readingStartRef.current) / 1000);
    readingStartRef.current = now;

    const percentage = Math.round((page / numPages) * 100);

    api
      .put(`/reading-progress/${documentId}`, {
        pageNumber: page,
        percentage,
        secondsReadDelta,
        isCompleted: percentage >= 100,
      })
      .catch((err) => {
        console.warn("reading-progress save failed:", err);
      });
  }, [page, ready, numPages, resumeLoaded, documentId]);

  /* ==================================================
     EXTEND RENDER LIMIT
     ================================================== */
  useEffect(() => {
    if (!ready || !numPages) return;
    const maxPage = allowedMaxPage ?? numPages;

    if (page >= renderLimit - 4) {
      defer(() => {
        setRenderLimit((prev) => Math.min(maxPage, prev + 10));
      });
    }
  }, [page, renderLimit, ready, numPages, allowedMaxPage]);

  /* ==================================================
     APPLY RESUME
     ================================================== */
  useEffect(() => {
    if (!ready || !numPages || !resumeLoaded) return;
    if (resumeAppliedRef.current) return;

    const targetRaw = resumeTargetRef.current;
    if (!targetRaw) return;

    const maxPage = allowedMaxPage ?? numPages;
    const target = Math.min(Math.max(1, Number(targetRaw)), maxPage);

    defer(() => {
      setRenderLimit((prev) => Math.max(prev, Math.min(target + 6, maxPage)));
      setPage(target);
      currentPageRef.current = target;
      emitPageChange(target);
    });

    let cancelled = false;

    const attemptScroll = () => {
      if (cancelled) return;

      isProgrammaticNavRef.current = true;
      const ok = scrollToPage(target, "auto");

      if (ok) {
        resumeAppliedRef.current = true;
        setTimeout(() => {
          isProgrammaticNavRef.current = false;
        }, 350);
        return;
      }
      setTimeout(attemptScroll, 80);
    };

    setTimeout(attemptScroll, 80);

    return () => {
      cancelled = true;
    };
  }, [ready, numPages, resumeLoaded, allowedMaxPage, scrollToPage, emitPageChange]);

  /* ==================================================
     SAFE PAGE SETTER
     ================================================== */
  const safeSetPage = useCallback(
    (nextPage) => {
      const max = allowedMaxPage ?? numPages ?? null;
      if (max && nextPage > max) {
        if (typeof onPreviewLimitReached === "function") onPreviewLimitReached();
        return;
      }

      isProgrammaticNavRef.current = true;
      setPage(nextPage);
      currentPageRef.current = nextPage;
      emitPageChange(nextPage);

      requestAnimationFrame(() => {
        scrollToPage(nextPage, "smooth");
        setTimeout(() => {
          isProgrammaticNavRef.current = false;
        }, 350);
      });
    },
    [allowedMaxPage, numPages, onPreviewLimitReached, scrollToPage, emitPageChange]
  );

  useEffect(() => {
    readingStartRef.current = Date.now();
  }, [documentId]);

  // ✅ clamp current page safely
  useEffect(() => {
    const max = allowedMaxPage ?? numPages ?? null;
    if (!max) return;
    if (page > max) safeSetPage(max);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedMaxPage, numPages]);

  /* ==================================================
     ✅ Unified page commit
     ================================================== */
  const commitDetectedPage = useCallback(
    (detectedPage) => {
      const p = Number(detectedPage);
      if (!Number.isFinite(p) || p <= 0) return;

      const maxPage = allowedMaxPage ?? numPages ?? null;
      if (maxPage && p > maxPage) return;

      if (isProgrammaticNavRef.current) return;

      if (lastSensorPageRef.current === p) return;
      lastSensorPageRef.current = p;

      currentPageRef.current = p;
      emitPageChange(p);

      clearTimeout(pageUpdateTimeoutRef.current);
      pageUpdateTimeoutRef.current = setTimeout(() => {
        setPage((prev) => (prev === p ? prev : p));
      }, 30);

      if (
        maxAllowedPage &&
        p === maxAllowedPage &&
        typeof onPreviewLimitReached === "function" &&
        !previewLimitTriggeredRef.current
      ) {
        previewLimitTriggeredRef.current = true;
        onPreviewLimitReached();
      }

      if (maxAllowedPage && p < maxAllowedPage) {
        previewLimitTriggeredRef.current = false;
      }
    },
    [allowedMaxPage, numPages, maxAllowedPage, onPreviewLimitReached, emitPageChange]
  );

  /* ==================================================
     Scroll fallback-only
     ================================================== */
  const computePageFromScroll = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root) return;

    const maxPage = allowedMaxPage ?? numPages ?? null;
    if (!maxPage) return;

    const rootRect = root.getBoundingClientRect();
    const anchorY = rootRect.top + 90;

    let bestPage = null;
    let bestDist = Infinity;

    const keys = Object.keys(pageElsRef.current)
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n) && n > 0 && n <= maxPage)
      .sort((a, b) => a - b);

    for (const pg of keys) {
      const el = pageElsRef.current[pg];
      if (!el) continue;

      const r = el.getBoundingClientRect();
      if (r.bottom < rootRect.top - 400) continue;
      if (r.top > rootRect.bottom + 400) break;

      const dist = Math.abs(r.top - anchorY);
      if (dist < bestDist) {
        bestDist = dist;
        bestPage = pg;
      }
    }

    if (!bestPage) return;
    commitDetectedPage(bestPage);
  }, [allowedMaxPage, numPages, commitDetectedPage]);

  const scheduleFallbackCompute = useCallback(() => {
    if (scrollRafRef.current) return;

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      computePageFromScroll();
    });
  }, [computePageFromScroll]);

  /* ==================================================
     INTERSECTION OBSERVER (authoritative)
     ================================================== */
  useEffect(() => {
    if (!ready) return;

    const root = scrollRootRef.current;
    if (!root) return;

    const pages = root.querySelectorAll(".pdf-page-wrapper");
    if (!pages.length) return;

    if (pageObserverRef.current) pageObserverRef.current.disconnect();

    observerReadyRef.current = true;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;

        const current = Number(visible.target.getAttribute("data-page-number"));
        if (!Number.isFinite(current)) return;

        commitDetectedPage(current);
      },
      { root, rootMargin: "-10% 0px -55% 0px", threshold: [0.15, 0.35, 0.55] }
    );

    pageObserverRef.current = observer;
    pages.forEach((pEl) => observer.observe(pEl));

    return () => {
      observer.disconnect();
      observerReadyRef.current = false;
    };
  }, [ready, zoom, renderLimit, allowedMaxPage, numPages, commitDetectedPage]);

  /* ==================================================
     NOTES: LOAD
     ================================================== */
  useEffect(() => {
    const ctrl = new AbortController();

    api
      .get(`/legal-document-notes/document/${documentId}`, { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setNotes(res.data || []);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        console.warn("notes load failed:", err);
        setNotes([]);
      });

    return () => {
      ctrl.abort();
    };
  }, [documentId]);

  /* ==================================================
     Go-to-page helpers
     ================================================== */
  const openPageJump = useCallback(() => {
    setPageJumpError("");
    setPageJumpValue(String(page));
    setShowPageJump(true);
    setShowFind(false);
  }, [page]);

  const submitPageJump = useCallback(() => {
    const raw = String(pageJumpValue || "").trim();
    const n = Number(raw);
    const max = allowedMaxPage ?? numPages ?? 1;

    if (!raw || Number.isNaN(n)) {
      setPageJumpError("Enter a valid page number.");
      return;
    }

    if (n < 1 || n > max) {
      setPageJumpError(`Page must be between 1 and ${max}.`);
      return;
    }

    setShowPageJump(false);
    setPageJumpError("");
    safeSetPage(n);
  }, [pageJumpValue, allowedMaxPage, numPages, safeSetPage]);

  useEffect(() => {
    if (!showPageJump) return;

    const t = setTimeout(() => {
      const el = pageJumpInputRef.current;
      if (el) {
        el.focus();
        try {
          el.select();
        } catch (err) {
          console.warn("page jump select failed:", err);
        }
      }
    }, 0);

    return () => clearTimeout(t);
  }, [showPageJump]);

  /* ==================================================
     Find in PDF (text extraction + page list)
     ================================================== */
  const openFind = useCallback(() => {
    setShowFind(true);
    setShowPageJump(false);
    setFindError("");
    setTimeout(() => {
      const el = findInputRef.current;
      if (el) {
        el.focus();
        try {
          el.select();
        } catch {
          //
        }
      }
    }, 0);
  }, []);

  const closeFind = useCallback(() => {
    setShowFind(false);
    setFindBusy(false);
    setFindError("");
  }, []);

  const extractPageText = useCallback(async (pdf, pageNumber) => {
    if (!pdf) return "";
    const cached = pageTextCacheRef.current.get(pageNumber);
    if (cached != null) return cached;

    const p = await pdf.getPage(pageNumber);
    const content = await p.getTextContent();
    const text = (content?.items || []).map((it) => it?.str || "").join(" ");
    pageTextCacheRef.current.set(pageNumber, text);
    return text;
  }, []);

  const runFind = useCallback(async () => {
    const q = String(findQuery || "").trim();
    if (!q) {
      setFindResults([]);
      setFindActiveIdx(0);
      setFindError("Type something to search.");
      return;
    }

    const pdf = pdfRef.current;
    if (!pdf) {
      setFindError("PDF not ready yet.");
      return;
    }

    const max = allowedMaxPage ?? numPages ?? 0;
    if (!max) return;

    setFindBusy(true);
    setFindError("");
    setFindResults([]);
    setFindActiveIdx(0);

    // cancel any previous run
    findAbortRef.current.aborted = true;
    findAbortRef.current = { aborted: false };

    const abortObj = findAbortRef.current;

    try {
      const hits = [];
      const needle = q.toLowerCase();

      // progressive scan (fast enough; cache helps a lot)
      for (let pg = 1; pg <= max; pg++) {
        if (abortObj.aborted) return;

        const text = await extractPageText(pdf, pg);
        if (String(text).toLowerCase().includes(needle)) hits.push(pg);

        // yield occasionally
        if (pg % 4 === 0) await new Promise((r) => setTimeout(r, 0));
      }

      if (abortObj.aborted) return;

      setFindResults(hits);
      if (hits.length === 0) {
        setFindError("No matches found.");
        return;
      }

      // jump to first match
      setFindActiveIdx(0);
      safeSetPage(hits[0]);
    } catch (err) {
      console.warn("find failed:", err);
      setFindError("Search failed. Try again.");
    } finally {
      setFindBusy(false);
    }
  }, [findQuery, allowedMaxPage, numPages, extractPageText, safeSetPage]);

  const goFindNext = useCallback(() => {
    if (!findResults.length) return;
    const next = (findActiveIdx + 1) % findResults.length;
    setFindActiveIdx(next);
    safeSetPage(findResults[next]);
  }, [findResults, findActiveIdx, safeSetPage]);

  const goFindPrev = useCallback(() => {
    if (!findResults.length) return;
    const prev = (findActiveIdx - 1 + findResults.length) % findResults.length;
    setFindActiveIdx(prev);
    safeSetPage(findResults[prev]);
  }, [findResults, findActiveIdx, safeSetPage]);

  /* ==================================================
     KEYBOARD SHORTCUTS
     ================================================== */
  useEffect(() => {
    function onKeyDown(e) {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const typing =
        tag === "input" ||
        tag === "textarea" ||
        document.activeElement?.isContentEditable;

      // Ctrl/Cmd+F should open our find (even if typing is false)
      const isFindCombo = (e.ctrlKey || e.metaKey) && (e.key === "f" || e.key === "F");
      if (isFindCombo) {
        e.preventDefault();
        openFind();
        return;
      }

      if (typing) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        safeSetPage(Math.max(1, page - 1));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        safeSetPage(Math.min((allowedMaxPage ?? numPages) || 1, page + 1));
      } else if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setShowNotes((v) => !v);
      } else if (e.key === "d" || e.key === "D") {
        e.preventDefault();
        setDarkMode((v) => !v);
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setZoom((z) => Math.min(1.6, Math.round((z + 0.1) * 10) / 10));
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        setZoom((z) => Math.max(0.7, Math.round((z - 0.1) * 10) / 10));
      } else if (e.key === "g" || e.key === "G") {
        e.preventDefault();
        openPageJump();
      } else if (e.key === "Escape") {
        if (showNoteBox) setShowNoteBox(false);
        if (showNotes) setShowNotes(false);
        if (showPageJump) setShowPageJump(false);
        if (showFind) closeFind();
      } else if (e.key === "Enter" && showFind) {
        // quick "enter to search"
        runFind();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    page,
    numPages,
    allowedMaxPage,
    showNoteBox,
    showNotes,
    showPageJump,
    showFind,
    safeSetPage,
    openPageJump,
    openFind,
    closeFind,
    runFind,
  ]);

  /* ==================================================
     Close popovers on outside click
     ================================================== */
  useEffect(() => {
    function onDown(e) {
      const pop = document.querySelector(".reader-popover");
      if (!pop) return;

      // if click is inside any popover, ignore
      if (pop.contains(e.target)) return;

      // close
      setShowPageJump(false);
      setShowFind(false);
      setPageJumpError("");
      setFindError("");
    }

    if (showPageJump || showFind) {
      window.addEventListener("mousedown", onDown);
      return () => window.removeEventListener("mousedown", onDown);
    }
  }, [showPageJump, showFind]);

  /* ==================================================
     Highlight capture
     ================================================== */
  const handleMouseUp = useCallback(() => {
    setTimeout(() => {
      if (isUserScrollingRef.current) return;

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) return;

      const text = selection.toString().trim();
      if (!text || text.length < 3) return;

      const range = selection.getRangeAt(0);
      const wrapper = getPageWrapperFromSelection(selection);
      if (!wrapper) return;

      const pageNumber = Number(wrapper.getAttribute("data-page-number"));
      if (!pageNumber || Number.isNaN(pageNumber)) return;

      const pageRect = wrapper.getBoundingClientRect();
      const clientRects = Array.from(range.getClientRects());
      if (!clientRects.length) return;

      const rects = clientRects.map((r) => ({
        x: r.left - pageRect.left,
        y: r.top - pageRect.top,
        width: r.width,
        height: r.height,
      }));

      let offsets = { start: null, end: null };
      try {
        const textLayerEl = wrapper.querySelector(".react-pdf__Page__textContent");
        if (textLayerEl) offsets = computeOffsetsFromRange(textLayerEl, range);
      } catch (err) {
        console.warn("offset compute failed (non-blocking):", err);
      }

      setSelectedText(text);
      setNoteContent("");

      setHighlightMeta({
        id:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : String(Date.now()),
        page: pageNumber,
        text,
        rects,
        charOffsetStart: offsets.start,
        charOffsetEnd: offsets.end,
      });

      setHighlightColor("yellow");
      setShowNoteBox(true);

      try {
        selection.removeAllRanges();
      } catch (err) {
        console.warn("selection clear failed:", err);
      }
    }, 30);
  }, []);

  /* ==================================================
     jumpToPage (for Notes + ToC)
     ================================================== */
  const jumpToPage = useCallback(
    (p, behavior = "smooth") => {
      const max = allowedMaxPage ?? numPages ?? null;
      if (!max) return false;

      const raw = Number(p);
      if (!Number.isFinite(raw) || raw <= 0) return false;

      const target = Math.min(Math.max(1, raw), max);

      if (maxAllowedPage && raw > maxAllowedPage) {
        if (typeof onPreviewLimitReached === "function") onPreviewLimitReached();
        return false;
      }

      setRenderLimit((prev) => Math.max(prev, Math.min(target + 6, max)));

      isProgrammaticNavRef.current = true;
      setPage(target);
      currentPageRef.current = target;
      emitPageChange(target);

      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToPage(target, behavior);

          setTimeout(() => {
            isProgrammaticNavRef.current = false;

            clearTimeout(fallbackIdleTimerRef.current);
            fallbackIdleTimerRef.current = setTimeout(() => {
              if (!observerReadyRef.current) scheduleFallbackCompute();
            }, 160);
          }, 350);
        }, 60);
      });

      return true;
    },
    [
      allowedMaxPage,
      numPages,
      maxAllowedPage,
      onPreviewLimitReached,
      scrollToPage,
      scheduleFallbackCompute,
      emitPageChange,
    ]
  );

  /* ==================================================
     ✅ Stable API registration for parent Reader
     ================================================== */
  const jumpToPageRef = useRef(jumpToPage);
  useEffect(() => {
    jumpToPageRef.current = jumpToPage;
  }, [jumpToPage]);

  useEffect(() => {
    if (typeof onRegisterApi !== "function") return;

    const apiObj = {
      jumpToPage: (p, behavior) => jumpToPageRef.current?.(p, behavior),
      getCurrentPage: () => currentPageRef.current,
      onPageChange: (fn) => {
        if (typeof fn !== "function") return () => {};
        pageChangeSubsRef.current.add(fn);
        return () => pageChangeSubsRef.current.delete(fn);
      },
    };

    onRegisterApi(apiObj);

    return () => {
      try {
        onRegisterApi(null);
      } catch (err) {
        console.warn("onRegisterApi(null) failed:", err);
      }
    };
  }, [onRegisterApi]);

  const focusNote = useCallback((noteId) => {
    setShowNotes(true);
    setActiveNoteId(noteId);

    setTimeout(() => {
      const el = noteRefs.current[noteId];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashNoteId(noteId);
        setTimeout(() => setFlashNoteId(null), 900);
      }
    }, 50);
  }, []);

  const attachHighlightClickHandler = useCallback(
    (textLayerEl) => {
      if (!textLayerEl) return;
      if (textLayerEl.dataset.hlClickBound === "1") return;
      textLayerEl.dataset.hlClickBound = "1";

      textLayerEl.addEventListener("click", (e) => {
        const mark = e.target?.closest?.("mark.pdf-highlight");
        if (!mark) return;

        const idStr = mark.dataset.noteId;
        const id = Number(idStr);
        if (!id || Number.isNaN(id)) return;

        focusNote(id);
      });
    },
    [focusNote]
  );

  const applyHighlightsForPage = useCallback(
    (pageNumber) => {
      const wrapper = pageElsRef.current[pageNumber];
      if (!wrapper) return;

      const textLayer = wrapper.querySelector(".react-pdf__Page__textContent");
      if (!textLayer) return;

      clearExistingMarks(textLayer);

      const pageNotes = (notes || []).filter((n) => n.pageNumber === pageNumber);

      for (const n of pageNotes) {
        if (n.charOffsetStart != null && n.charOffsetEnd != null) {
          applyHighlightByOffsets(
            textLayer,
            n.charOffsetStart,
            n.charOffsetEnd,
            n.id,
            n.highlightColor || "yellow"
          );
        }
      }

      // apply find highlight on the current page only
      if (showFind && findQuery && (findResults?.includes?.(pageNumber) || pageNumber === page)) {
        applyFindHighlight(textLayer, findQuery);
      } else {
        clearFindMarks(textLayer);
      }

      attachHighlightClickHandler(textLayer);
    },
    [notes, attachHighlightClickHandler, showFind, findQuery, findResults, page]
  );

  useEffect(() => {
    if (!ready) return;

    const cap = Math.min(renderLimit, allowedMaxPage ?? renderLimit);
    for (let pg = 1; pg <= cap; pg++) applyHighlightsForPage(pg);
  }, [ready, renderLimit, allowedMaxPage, applyHighlightsForPage]);

  const hasOverlapOnPage = useCallback(() => false, []);

  const resetNoteDraft = useCallback(() => {
    setShowNoteBox(false);
    setSelectedText("");
    setNoteContent("");
    setHighlightMeta(null);
  }, []);

  async function saveNote() {
    if (!highlightMeta) return;

    if (hasOverlapOnPage(highlightMeta)) {
      alert("A highlight already exists in that selected range.");
      return;
    }

    try {
      const finalContent =
        noteContent && noteContent.trim().length > 0 ? noteContent.trim() : highlightMeta.text;

      await api.post("/legal-document-notes", {
        legalDocumentId: Number(documentId),
        highlightedText: highlightMeta.text,
        pageNumber: highlightMeta.page,
        charOffsetStart: highlightMeta.charOffsetStart,
        charOffsetEnd: highlightMeta.charOffsetEnd,
        content: finalContent,
        highlightColor: highlightColor,
      });

      const res = await api.get(`/legal-document-notes/document/${documentId}`);
      setNotes(res.data || []);
      setShowNotes(true);

      setHighlights((prev) => [
        ...prev,
        {
          id: highlightMeta.id,
          page: highlightMeta.page,
          rects: highlightMeta.rects,
          color:
            highlightColor === "blue"
              ? "rgba(59,130,246,0.30)"
              : highlightColor === "pink"
              ? "rgba(236,72,153,0.30)"
              : highlightColor === "green"
              ? "rgba(34,197,94,0.28)"
              : "rgba(255, 235, 59, 0.45)",
        },
      ]);

      setTimeout(() => applyHighlightsForPage(highlightMeta.page), 80);
      resetNoteDraft();
    } catch (err) {
      console.error("Failed to save note:", err);
      alert(
        err?.response?.data?.message ||
          JSON.stringify(err?.response?.data) ||
          "Failed to save note. Please try again."
      );
      resetNoteDraft();
    }
  }

  async function saveEdit(noteId) {
    try {
      await api.put(`/legal-document-notes/${noteId}`, { content: editingContent });
      setNotes((ns) => ns.map((n) => (n.id === noteId ? { ...n, content: editingContent } : n)));
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          JSON.stringify(err?.response?.data) ||
          "Failed to update note."
      );
    }

    setEditingNoteId(null);
    setEditingContent("");
  }

  async function deleteNote(noteId) {
    const ok = confirm("Delete this note?");
    if (!ok) return;

    try {
      await api.delete(`/legal-document-notes/${noteId}`);
      setNotes((ns) => ns.filter((n) => n.id !== noteId));
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          JSON.stringify(err?.response?.data) ||
          "Failed to delete note."
      );
    }
  }

  const groupedNotes = useMemo(() => {
    const map = new Map();

    for (const n of notes || []) {
      const key = n.pageNumber ?? -1;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    }

    for (const [, arr] of map.entries()) {
      arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === -1) return 1;
      if (b === -1) return -1;
      return a - b;
    });

    return keys.map((k) => ({
      pageNumber: k === -1 ? null : k,
      items: map.get(k),
    }));
  }, [notes]);

  /* ==================================================
     Render
     ================================================== */
  const effectiveRenderCap = useMemo(() => {
    const cap = allowedMaxPage ?? numPages ?? null;
    if (!cap) return renderLimit;
    return Math.min(renderLimit, cap);
  }, [allowedMaxPage, numPages, renderLimit]);

  return (
    <div className={`reader-shell ${darkMode ? "dark" : ""}`}>
      {/* Minimal “glass” top bar */}
      <div className="reader-topbar">
        <div className="reader-topbar-inner">
          <div className="reader-topbar-left">
            <div className="reader-chip" title="Current page">
              {page} / {numPages || "—"}
            </div>
          </div>

          <div className="reader-topbar-right">
            <button
              className="icon-btn"
              onClick={() => setZoom((z) => Math.max(0.7, Math.round((z - 0.1) * 10) / 10))}
              title="Zoom out (-)"
              type="button"
            >
              −
            </button>

            <div className="reader-chip" title="Zoom">
              {Math.round(zoom * 100)}%
            </div>

            <button
              className="icon-btn"
              onClick={() => setZoom((z) => Math.min(1.6, Math.round((z + 0.1) * 10) / 10))}
              title="Zoom in (+)"
              type="button"
            >
              +
            </button>

            {/* Find */}
            <button
              className={`icon-btn ${showFind ? "active" : ""}`}
              onClick={() => (showFind ? closeFind() : openFind())}
              title="Find in PDF (Ctrl/⌘ + F)"
              type="button"
            >
              ⌕
            </button>

            {/* Go to page */}
            <button
              className={`icon-btn ${showPageJump ? "active" : ""}`}
              onClick={openPageJump}
              title="Go to page (G)"
              type="button"
            >
              #️⃣
            </button>

            <button
              className="icon-btn"
              onClick={() => setDarkMode((d) => !d)}
              title={darkMode ? "Light mode (D)" : "Dark mode (D)"}
              type="button"
            >
              {darkMode ? "☀️" : "🌙"}
            </button>

            <button
              className={`icon-btn ${showNotes ? "active" : ""}`}
              onClick={() => setShowNotes((v) => !v)}
              title="Notes (N)"
              type="button"
            >
              📝
            </button>

            {/* Find popover (premium, not oval) */}
            {showFind && (
              <div
                className="reader-popover"
                role="dialog"
                aria-modal="false"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="reader-popover-head">
                  <div className="reader-popover-title">Find in document</div>
                  <button className="reader-popover-x" onClick={closeFind} type="button" title="Close">
                    ✕
                  </button>
                </div>

                <div className="reader-popover-row">
                  <input
                    ref={findInputRef}
                    className="reader-popover-input"
                    value={findQuery}
                    onChange={(e) => setFindQuery(e.target.value)}
                    placeholder="Type to search…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") runFind();
                      if (e.key === "Escape") closeFind();
                    }}
                  />
                  <button
                    className="reader-popover-btn primary"
                    onClick={runFind}
                    type="button"
                    disabled={findBusy}
                    title="Search"
                  >
                    {findBusy ? "…" : "Go"}
                  </button>
                </div>

                <div className="reader-popover-actions">
                  <button
                    className="reader-popover-btn"
                    onClick={goFindPrev}
                    type="button"
                    disabled={!findResults.length || findBusy}
                    title="Previous match"
                  >
                    Prev
                  </button>
                  <button
                    className="reader-popover-btn"
                    onClick={goFindNext}
                    type="button"
                    disabled={!findResults.length || findBusy}
                    title="Next match"
                  >
                    Next
                  </button>

                  <div className="reader-popover-meta" title="Matches">
                    {findResults.length
                      ? `${findActiveIdx + 1} / ${findResults.length}`
                      : "0 / 0"}
                  </div>
                </div>

                {!!findError && <div className="reader-popover-error">{findError}</div>}

                <div className="reader-popover-hint">
                  Tip: Use <b>Enter</b> to search, <b>Prev/Next</b> to jump matches.
                </div>
              </div>
            )}

            {/* Page jump popover (small + clear) */}
            {showPageJump && (
              <div
                className="reader-popover"
                role="dialog"
                aria-modal="false"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="reader-popover-head">
                  <div className="reader-popover-title">Go to page</div>
                  <button
                    className="reader-popover-x"
                    onClick={() => setShowPageJump(false)}
                    title="Close"
                    type="button"
                  >
                    ✕
                  </button>
                </div>

                <div className="reader-popover-row">
                  <input
                    ref={pageJumpInputRef}
                    className="reader-popover-input"
                    value={pageJumpValue}
                    onChange={(e) => setPageJumpValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitPageJump();
                      if (e.key === "Escape") setShowPageJump(false);
                    }}
                    inputMode="numeric"
                    placeholder={`1 - ${allowedMaxPage ?? numPages ?? "?"}`}
                    autoFocus
                  />
                  <button
                    className="reader-popover-btn primary"
                    onClick={submitPageJump}
                    title="Go"
                    type="button"
                  >
                    Go
                  </button>
                </div>

                {!!pageJumpError && <div className="reader-popover-error">{pageJumpError}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="reader-container">
        <div
          className="reader-scroll"
          ref={scrollRootRef}
          onMouseUp={handleMouseUp}
          onScroll={() => {
            if (!ready) return;

            if (!isProgrammaticNavRef.current) {
              clearTimeout(fallbackIdleTimerRef.current);
              fallbackIdleTimerRef.current = setTimeout(() => {
                scheduleFallbackCompute();
              }, 140);
            }

            isUserScrollingRef.current = true;
            clearTimeout(snapTimeoutRef.current);
            snapTimeoutRef.current = setTimeout(() => {
              isUserScrollingRef.current = false;
            }, 120);
          }}
        >
          {!ready && !!loadError && (
            <div className="reader-loadErrorWrap">
              <div className="reader-loadErrorCard">
                <div className="reader-loadErrorText">{loadError}</div>
                <div className="reader-loadErrorActions">
                  <button className="outline-btn" onClick={() => window.location.reload()} type="button">
                    Refresh
                  </button>
                  <button
                    className="outline-btn"
                    onClick={() => {
                      setLoadError("Reloading in compatibility mode…");
                      setSafeMode(true);
                      setReady(false);
                      setNumPages(null);
                      setRenderLimit(10);
                      setDocKey((k) => k + 1);
                    }}
                    type="button"
                  >
                    Try compatibility mode
                  </button>
                </div>
              </div>
            </div>
          )}

          {!ready && (
            <div className="reader-loadingOverlay">
              <div className="reader-loadingCard">
                <div className="reader-loadingTitle">
                  Loading document… {Math.min(99, Math.max(0, loadPct))}%
                </div>
                <div className="reader-loadingHint">
                  Preparing pages for reading…{safeMode ? " (compatibility mode)" : ""}
                </div>
                <div className="reader-progressBar">
                  <div
                    className="reader-progressBarFill"
                    style={{ width: `${Math.min(99, Math.max(0, loadPct))}%` }}
                  />
                </div>

                {!!loadError && <div className="reader-loadingError">{loadError}</div>}
              </div>
            </div>
          )}

          <Document
            key={docKey}
            file={fileSource}
            options={pdfOptions}
            onLoadProgress={({ loaded, total }) => {
              if (total && total > 0) setLoadPct(Math.round((loaded / total) * 100));
            }}
            onLoadError={(err) => {
              console.error("react-pdf onLoadError:", err, "fileSource=", fileSource);

              if (!safeMode && looksLikeRangeCacheFailure(err)) {
                setLoadError("Reloading in compatibility mode…");
                setSafeMode(true);
                setReady(false);
                setNumPages(null);
                setRenderLimit(10);
                setDocKey((k) => k + 1);
                return;
              }

              setReady(false);
              setNumPages(null);
              setLoadError("Failed to load PDF. Please refresh and try again.");
            }}
            onSourceError={(err) => {
              console.error("react-pdf onSourceError:", err, "fileSource=", fileSource);

              if (!safeMode && looksLikeRangeCacheFailure(err)) {
                setLoadError("Reloading in compatibility mode…");
                setSafeMode(true);
                setReady(false);
                setNumPages(null);
                setRenderLimit(10);
                setDocKey((k) => k + 1);
                return;
              }

              setReady(false);
              setNumPages(null);
              setLoadError("Unable to load document source.");
            }}
            onLoadSuccess={(pdf) => {
              // IMPORTANT: keep pdf proxy for Find
              pdfRef.current = pdf;

              const loadedPages = pdf?.numPages ?? null;
              setNumPages(loadedPages);

              const allowed = maxAllowedPage ? Math.min(maxAllowedPage, loadedPages) : loadedPages;

              let initial = startPage || 1;
              if (pendingJumpRef.current != null) {
                initial = pendingJumpRef.current;
                pendingJumpRef.current = null;
              }

              initial = Math.min(Math.max(1, initial), allowed);

              setPage(initial);
              currentPageRef.current = initial;
              emitPageChange(initial);

              setRenderLimit(Math.min(allowed, Math.max(initial + 6, 10)));
              setLoadPct(100);
              setLoadError("");
              setReady(true);

              isProgrammaticNavRef.current = true;
              setTimeout(() => {
                scrollToPage(initial, "auto");
                setTimeout(() => {
                  isProgrammaticNavRef.current = false;
                  clearTimeout(fallbackIdleTimerRef.current);
                  fallbackIdleTimerRef.current = setTimeout(() => scheduleFallbackCompute(), 160);
                }, 250);
              }, 60);
            }}
          >
            {ready &&
              Array.from({ length: effectiveRenderCap }, (_, i) => {
                const pageNumber = i + 1;

                return (
                  <div
                    key={pageNumber}
                    className="pdf-page-wrapper"
                    data-page-number={pageNumber}
                    ref={(el) => registerPageEl(pageNumber, el)}
                  >
                    <svg className="highlight-layer" width="100%" height="100%">
                      {(highlightsByPage?.[pageNumber] || []).flatMap((h) =>
                        (h.rects || []).map((r, idx) => (
                          <rect
                            key={`${h.id}-${idx}`}
                            x={r.x}
                            y={r.y}
                            width={r.width}
                            height={r.height}
                            rx="2"
                            fill={h.color || "rgba(255, 235, 59, 0.45)"}
                          />
                        ))
                      )}
                    </svg>

                    <Page
                      pageNumber={pageNumber}
                      width={Math.round(820 * zoom)}
                      renderTextLayer
                      renderAnnotationLayer={false}
                      loading={<div className="reader-pageLoading">Rendering page {pageNumber}…</div>}
                      onRenderTextLayerSuccess={() => {
                        applyHighlightsForPage(pageNumber);
                        clearTimeout(fallbackIdleTimerRef.current);
                        fallbackIdleTimerRef.current = setTimeout(() => scheduleFallbackCompute(), 120);
                      }}
                    />
                  </div>
                );
              })}
          </Document>

          <div className="reader-bottomSpacer" />
        </div>
      </div>

      {/* Floating compact navigation pill */}
      <div className="reader-fab">
        <button
          className="fab-btn"
          onClick={() => safeSetPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          title="Previous page (←)"
          type="button"
        >
          ◀
        </button>

        <button className="fab-mid" onClick={openPageJump} title="Go to page (G)" type="button">
          {page} / {numPages || "—"}
        </button>

        <button
          className="fab-btn"
          onClick={() => safeSetPage(Math.min((allowedMaxPage ?? numPages) || 1, page + 1))}
          disabled={numPages ? page >= (allowedMaxPage ?? numPages) : true}
          title="Next page (→)"
          type="button"
        >
          ▶
        </button>
      </div>

      {/* NOTE OVERLAY + Notes sidebar */}
      {showNoteBox && (
        <div className="note-overlay">
          <div className="note-box">
            <h4>Add note</h4>
            <p className="note-preview">“{selectedText.slice(0, 120)}…”</p>

            <div className="hl-color-row">
              <span className="hl-color-label">Highlight:</span>
              {["yellow", "blue", "pink", "green"].map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`hl-color-chip ${c} ${highlightColor === c ? "active" : ""}`}
                  onClick={() => setHighlightColor(c)}
                  title={c}
                />
              ))}
            </div>

            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Write your note (optional)…"
            />

            <div className="note-actions">
              <button className="reader-btn secondary" onClick={resetNoteDraft} type="button">
                Cancel
              </button>

              <button className="reader-btn" onClick={saveNote} type="button">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotes && <div className="notes-backdrop" onClick={() => setShowNotes(false)} />}

      <div className={`notes-sidebar ${showNotes ? "open" : "closed"}`}>
        <div className="notes-header">
          <h3>Notes</h3>
          <button onClick={() => setShowNotes(false)} title="Close (Esc)" type="button">
            ✕
          </button>
        </div>

        <div className="notes-list">
          {groupedNotes.length === 0 ? (
            <div className="notes-empty">
              <p>No notes yet</p>
              <p className="notes-empty-hint">Select text in the document to add your first note.</p>
            </div>
          ) : (
            groupedNotes.map((group) => (
              <div key={group.pageNumber ?? "unknown"} className="notes-group">
                <div className="notes-group-title">Page {group.pageNumber ?? "—"}</div>

                {group.items.map((note) => (
                  <div
                    key={note.id}
                    className={[
                      "note-item",
                      note.id === activeNoteId ? "active" : "",
                      note.id === flashNoteId ? "flash" : "",
                    ].join(" ")}
                    ref={(el) => {
                      if (el) noteRefs.current[note.id] = el;
                    }}
                  >
                    <div className="note-meta" onClick={() => jumpToPage(note.pageNumber)} role="button" tabIndex={0}>
                      <span className={`note-color-dot ${note.highlightColor || "yellow"}`} />
                      Page {note.pageNumber ?? "—"}
                    </div>

                    {editingNoteId === note.id ? (
                      <>
                        <textarea
                          className="note-edit"
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                        />
                        <div className="note-actions-inline">
                          <button onClick={() => saveEdit(note.id)} type="button">
                            💾
                          </button>
                          <button
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingContent("");
                            }}
                            type="button"
                          >
                            ✕
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="note-text">{note.content}</div>
                        <div className="note-actions-inline">
                          <button
                            onClick={() => {
                              setEditingNoteId(note.id);
                              setEditingContent(note.content);
                            }}
                            type="button"
                          >
                            ✏️
                          </button>
                          <button onClick={() => deleteNote(note.id)} type="button">
                            🗑️
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
